import { of, ReplaySubject, Subject, Subscription, throwError } from 'rxjs';
import { Directive, inject, isDevMode, Input, OnChanges, OnDestroy } from '@angular/core';
import { Attributes, BaseDashboardConfig } from './base-dashboard-config';
import { ActivatedRoute, Router } from '@angular/router';
import { UrlService } from '../../../services/url.service';
import { CompoundFilterExpression, DataResponse, FilterExpression, parseTreeJson } from '../../../services/data.service';
import { isUndefined } from 'lodash-es';
import { DashboardContext, hasDataRoot } from './dashboard-context';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Level, PartialPath, unnest } from '../../../services/data-tree';
import { getInitialAttributes } from '../data-tree-table/data-tree-table';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import { combineLatestEmptySafe } from '@cumlaude/shared-utils';
import { DatasnapUrlService } from '../../../services/datasnap-url.service';
import { DataErrorMessage, RDatasnapResult } from '@cumlaude/service-contract';
import { GroeperingService } from '../../../services/groepering.service';
import { AggregatorConfig } from './aggregator-config';
import { AttrPath } from '@cumlaude/metadata';
import { toSignal } from '@angular/core/rxjs-interop';
import { isDataErrorMessage, isHappyContext, parseError } from '../../../services/error-util';

@Directive()
export abstract class BaseDashboardComponent<I extends Attributes, A extends Attributes, C extends BaseDashboardConfig<I, A>>
	implements OnChanges, OnDestroy
{
	protected readonly router = inject(Router);
	private readonly routeData = toSignal(inject(ActivatedRoute).data);
	protected readonly urlService = inject(UrlService);
	protected readonly envConfig: EnvConfiguration = inject(ENV_CONFIG);
	protected readonly qp: QueryParamStateService = inject(QueryParamStateService);
	protected readonly datasnapUrlService = inject(DatasnapUrlService);
	protected readonly groeperingService = inject(GroeperingService);

	@Input()
	tableGroups: AttrPath[] | undefined;

	/**
	 * Ingestelde groeperingen. Deze kunnen door de gebruiker zijn ingesteld of fixed door dit dashboard,
	 * of een combinatie van die 2.
	 */
	@Input()
	groups: AttrPath[] = [];

	/**
	 * Subgroups zijn groeperingen die op 1 rij in de tabel worden weergegeven. Measures worden wel voor iedere
	 * waarde van zo'n subgroup bepaald/uitgerekend.
	 */
	@Input()
	subgroups: AttrPath[] = [];

	/**
	 * Ingestelde filters. Deze kunnen door de gebruiker zijn ingesteld of fixed door dit dashboard,
	 * of een combinatie van die 2.
	 */
	@Input()
	filters?: FilterExpression[];

	/**
	 * Permanent ingestelde filters. Deze worden bepaald door het dashboard en kunnen niet gezien of aangepast worden door de gebruiker.
	 */
	@Input()
	permanentFilters: FilterExpression[] = [];

	/**
	 * Configuratie voor dashboard type.
	 */
	@Input()
	config!: C;

	/**
	 * Het kan voorkomen dat bijv. de groups en de subgroups tegelijk veranderen. Om te voorkomen dat er het component
	 * zichzelf dan 2x update (2 requests naar de backend) worden alle (veranderlijke) inputs nu in 1 observable gebundeld.
	 */
	protected inputs$ = new ReplaySubject<[AttrPath[], CompoundFilterExpression, AttrPath[]]>(1);

	protected dashboardContext$ = new Subject<DashboardContext<I, A, C> | DataErrorMessage>();

	protected subscriptions: Subscription[] = [];

	private subgroepenGemeld = false;

	protected constructor() {
		this.subscriptions.push(
			combineLatestEmptySafe([this.inputs$, this.datasnapUrlService.currentSnapshotKey$])
				.pipe(
					switchMap(([[g, f, s], snapshot]) => {
						if (snapshot) {
							return this.datasnapUrlService.getResult(snapshot).pipe(
								map((resp) => this.parseDatasnapshotResult(resp, f)),
								catchError((err) => {
									if (isDataErrorMessage(err)) return of(err);

									return throwError(() => err);
								})
							);
						}

						let groups = [...g, ...s];
						if (!isUndefined(this.tableGroups)) groups = [...this.tableGroups, ...groups];

						return this.config
							.getData({
								g: groups,
								f,
							})
							.pipe(
								map((resp) => this.createDashboardContext(resp, f)),
								catchError((err) => {
									if (isDataErrorMessage(err)) return of(err);

									return throwError(() => err);
								})
							);
					})
				)
				.subscribe((context) => {
					this.dashboardContext$.next(context);
				}),
			this.dashboardContext$.subscribe((context) => {
				if (isHappyContext(context) && hasDataRoot(context)) {
					this.config.dataRootSelection.set(context.config.createDataRootSelection(context));
				} else {
					this.config.dataRootSelection.set(undefined);
				}
				if (isHappyContext(context)) this.config.onContextCreated(context);
			})
		);
	}

	private parseDatasnapshotResult(resp: RDatasnapResult, f: FilterExpression) {
		if (resp.responseCode < 400) return this.createDashboardContext(parseTreeJson(resp.responseBody), f);

		return parseError(resp.responseBody);
	}

	ngOnChanges() {
		if (this.filters === undefined) return;

		if (isDevMode()) this.controleerSubgroepenUitRegistry();

		const filters = new CompoundFilterExpression([...this.permanentFilters, ...this.filters]);
		this.inputs$.next([this.groups, filters, this.subgroups]);
	}

	/**
	 * De subgroepen in de dashboard-registry zijn een samenvatting voor de AI-chat: ze vertellen het
	 * model waarop dit dashboard al uitsplitst, zodat het daar niet nog eens op groepeert. Ze zijn
	 * bewust niet de bron van `subgroups` hierboven, want die bevat ook technische niveaus en wordt
	 * vaak berekend uit de gekozen weergave. Deze controle bewaakt daarom alleen dat de samenvatting
	 * niets claimt wat het dashboard niet doet.
	 */
	private controleerSubgroepenUitRegistry() {
		const uitRegistry: string[] | undefined = this.routeData()?.['subgroepen'];
		if (!uitRegistry || this.subgroepenGemeld) return;

		const werkelijk = this.subgroups.map((pad) => pad.join('.'));
		const ontbreekt = uitRegistry.filter((att) => !werkelijk.includes(att));
		if (ontbreekt.length === 0) return;

		this.subgroepenGemeld = true;
		console.error(
			`Dashboard-registry noemt ${ontbreekt.join(', ')} als subgroepen, maar dit dashboard splitst uit naar ${werkelijk.join(', ') || 'niets'}. ` +
				'Werk de registry bij, anders krijgt de AI-chat verouderde informatie.'
		);
	}

	ngOnDestroy() {
		for (const sub of this.subscriptions) sub.unsubscribe();
	}

	protected createDashboardContext(resp: DataResponse<number[]>, f: FilterExpression): DashboardContext<I, A, C> {
		const { groupNames, subgroupNames, measureNames } = this.getNames(resp);
		const ngroups = groupNames.length;
		const { aggInit, aggCombine } = createAggFunctions<I, A>(this.config, subgroupNames, measureNames);

		const records = unnest<A, number[]>(resp.data, undefined, ngroups, aggInit, aggCombine);
		const dataRoot = records[0]?.[0];
		const orderRoot = resp.order ? unnest<unknown, []>(resp.order)[0]?.[0] : undefined;
		return {
			config: this.copyConfig(this.config),
			filter: f,
			tableRootIndex: <0 | 1 | 3>this.tableGroups?.length ?? 0,
			groupNames,
			subgroupNames,
			measureNames,
			dataRoot,
			aggCombine,
			orderRoot,
		};
	}

	protected getNames(resp: DataResponse<number[]>) {
		// haal de groups op uit de metadata van de response, want de groups die de gebruiker heeft aangeklikt
		// kunnen ondertussen weer gewijzigd zijn
		// de groepnamen in de context zijn de gepunte vorm van het pad, zoals ze ook in de configs worden gebruikt
		const g = resp.g.map((path) => path.join('.'));
		const groupNames = g.slice(0, g.length - this.subgroups.length);
		const subgroupNames = g.slice(g.length - this.subgroups.length);
		const measureNames = resp.measures;
		return { groupNames, subgroupNames, measureNames };
	}

	/**
	 * Maakt een kopie van de configuratie zodat de functies werken met de variabel waardes die op dat moment relevant zijn.
	 */
	private copyConfig(config: C) {
		return Object.assign(Object.create(Object.getPrototypeOf(config)), config);
	}
}

export function createAggFunctions<I extends Attributes, A extends Attributes>(
	config: AggregatorConfig<I, A>,
	subgroupNames: string[],
	measureNames: string[]
) {
	const singleAggregators = config.getSingleAggregators();
	const multiAggregators = config.getMultiAggregators();

	// construeer het "a" object op het diepste data-niveau (1 gekleurd balkje)
	function aggInit(data: number[], path: PartialPath<A, number[]>): A {
		const initialAttributes = getInitialAttributes<I>(subgroupNames, measureNames, path);
		const ret: A = <A>{};
		for (const attName in singleAggregators) {
			ret[attName] = singleAggregators[attName]!.init(
				initialAttributes,
				path.slice(1).map((level) => level.k)
			);
		}
		for (const aggregator of multiAggregators) {
			ret[aggregator.attribute] = aggregator.init(
				initialAttributes,
				path.slice(1).map((level) => level.k)
			);
		}
		return ret;
	}

	// aggregeer de "a" measures, werkt zowel binnen de barchart als binnen de tabel
	function aggCombine(c: Level<A, number[]>[], path: PartialPath<A, number[]>, all: A | undefined, allFiltered: A | undefined): A {
		const ret: A = <A>{};

		const childrenAttributes = c.map((child) => child.a);
		const keys = path.slice(1).map((level) => level.k);

		for (const attName in singleAggregators) {
			ret[attName] = singleAggregators[attName]!.combine(
				childrenAttributes.map((ca) => ca[attName]),
				keys,
				all?.[attName],
				allFiltered?.[attName]
			);
		}
		for (const aggregator of multiAggregators) {
			ret[aggregator.attribute] = aggregator.combine(
				childrenAttributes,
				keys,
				all?.[aggregator.attribute],
				allFiltered?.[aggregator.attribute]
			);
		}

		return ret;
	}

	return { aggInit, aggCombine };
}
